/**
 * Adding IndexAlpha to a phone's home screen (ROADMAP "Add IndexAlpha to the
 * home screen", owner pick 2026-09-30: after a value moment, never on landing).
 *
 * Two platforms, two mechanisms, per the platform guidance (web.dev
 * "customize-install" / "promote-install"; MDN "Making PWAs installable"):
 *
 *  - **Android / Chrome** fires `beforeinstallprompt` once it judges the app
 *    installable. We catch it, stop the browser's own mini-infobar, and keep
 *    the event so OUR button can call `prompt()` — once; it cannot be reused.
 *  - **iOS Safari** has no such event. The only route is Share → Add to Home
 *    Screen, so we show those two steps. In-app browsers (Gmail, Instagram…)
 *    have no such menu at all, so nothing is offered there.
 *
 * Never offered when already running installed. A dismissal holds for
 * DISMISS_DAYS; storage that cannot be read means "offer nothing", because an
 * unasked-for prompt is the worse failure.
 */

export interface DeferredInstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const INSTALL_DISMISSED_KEY = 'ia:installPromptDismissedAt';
export const DISMISS_DAYS = 60;
/** Set by the welcome sheet when it opens, so the two never share a visit. */
export const WELCOME_SHOWN_THIS_VISIT_KEY = 'ia:welcomeSheetShownThisVisit';
/** The page view of the visit on which the sheet may appear (welcome is the 2nd). */
export const INSTALL_SHEET_VIEW = 3;

let deferred: DeferredInstallPrompt | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

/** Start listening. Called once at boot, before the browser can fire the event. */
export function listenForInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // our button, at our moment — not the browser's infobar
    deferred = e as DeferredInstallPrompt;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

export function onInstallAvailabilityChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function hasDeferredPrompt(): boolean {
  return deferred !== null;
}

/** Show the browser's own install dialog. Usable once; returns the outcome. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (!event) return 'unavailable';
  deferred = null; // a prompt event cannot be reused
  notify();
  await event.prompt();
  const choice = await event.userChoice;
  return choice.outcome;
}

export function isRunningInstalled(): boolean {
  try {
    const standalone = typeof window.matchMedia === 'function'
      && window.matchMedia('(display-mode: standalone)').matches;
    const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    return standalone || iosStandalone;
  } catch {
    return false;
  }
}

/** In-app browsers: no Add to Home Screen menu, so nothing to offer. */
const IN_APP = /FBAN|FBAV|Instagram|Line\/|GSA\/|Twitter|LinkedInApp|Snapchat|Pinterest|; wv\)/i;

/** Real Safari on an iPhone or iPad — not Chrome/Firefox/Edge on iOS, not a webview. */
export function isIosSafari(ua: string = navigator.userAgent): boolean {
  const ios = /iPhone|iPad|iPod/.test(ua);
  const otherBrowser = /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return ios && !otherBrowser && /Safari\//.test(ua) && !IN_APP.test(ua);
}

export type InstallRoute = 'prompt' | 'ios-steps' | 'none';

/** How this browser can install the app right now. */
export function installRoute(ua: string = navigator.userAgent): InstallRoute {
  if (isRunningInstalled()) return 'none';
  if (hasDeferredPrompt()) return 'prompt';
  if (isIosSafari(ua)) return 'ios-steps';
  return 'none';
}

export function recentlyDismissed(now: number = Date.now()): boolean {
  try {
    const at = Number(localStorage.getItem(INSTALL_DISMISSED_KEY) || 0);
    return at > 0 && now - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return true;
  }
}

export function rememberInstallDismissed(now: number = Date.now()): void {
  try {
    localStorage.setItem(INSTALL_DISMISSED_KEY, String(now));
  } catch {
    // Storage blocked: `recentlyDismissed` already answers yes.
  }
}

export function welcomeShownThisVisit(): boolean {
  try {
    return sessionStorage.getItem(WELCOME_SHOWN_THIS_VISIT_KEY) !== null;
  } catch {
    return true;
  }
}

export function markWelcomeShownThisVisit(): void {
  try {
    sessionStorage.setItem(WELCOME_SHOWN_THIS_VISIT_KEY, '1');
  } catch {
    // Nothing to do — the install sheet then stays away this visit.
  }
}

/** Test seam: forget any captured event. */
export function __resetInstallPromptForTests(): void {
  deferred = null;
  listeners.clear();
}
